import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { useDocumentDarkClass } from '../../hooks/useDocumentDarkClass';
import { type Snapshot, type Stage } from './helpers';
import { STAGE_COLORS, ALL_STAGE_DEFS } from './pipelineLayout';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Filler, Tooltip, Legend, Title);

const STAGE_LABELS = new Map(ALL_STAGE_DEFS.map((s) => [s.id, s.label] as const));
const STAGE_ORDER = Array.from(STAGE_LABELS.keys());
const FALLBACK_COLOR = '#94a3b8';

export interface SecondaryLine {
  label: string;
  axisTitle: string;
  getTotal: (snapshot: Snapshot) => number;
}

/**
 * Stacked area over the accumulated snapshot history, one band per stage
 * (only stages with a nonzero value somewhere in the window), with an
 * optional dashed total line on a secondary right-hand axis.
 */
export default function StageAreaChart({
  history,
  getValue,
  yTitle,
  secondary,
}: {
  history: Snapshot[];
  getValue: (stage: Stage) => number;
  yTitle: string;
  secondary?: SecondaryLine;
}) {
  const isDark = useDocumentDarkClass();
  const labels = history.map((s) => new Date(s.generatedAt).toLocaleTimeString());
  // Sorted by pipeline order so dataset positions stay stable as the
  // rolling window trims history (chart.js reconciles datasets by index)
  const activeStageIds = Array.from(
    new Set(history.flatMap((s) => s.stages.filter((st) => getValue(st) > 0).map((st) => st.id))),
  ).sort((a, b) => STAGE_ORDER.indexOf(a) - STAGE_ORDER.indexOf(b));
  const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const tickColor = isDark ? '#a1a1aa' : '#6b7280';
  return (
    <div className="h-64">
      <Line
        data={{
          labels,
          datasets: [
            ...activeStageIds.map((id) => ({
              label: STAGE_LABELS.get(id) ?? id,
              data: history.map((s) => {
                const stage = s.stages.find((st) => st.id === id);
                return stage ? getValue(stage) : 0;
              }),
              borderColor: STAGE_COLORS[id] ?? FALLBACK_COLOR,
              backgroundColor: `${STAGE_COLORS[id] ?? FALLBACK_COLOR}66`,
              fill: true,
              stack: 'stages',
              tension: 0.3,
              pointRadius: 0,
              borderWidth: 1.5,
              yAxisID: 'y',
            })),
            ...(secondary
              ? [
                  {
                    label: secondary.label,
                    data: history.map((s) => secondary.getTotal(s)),
                    borderColor: isDark ? '#e4e4e7' : '#18181b',
                    backgroundColor: 'rgba(0,0,0,0)',
                    borderDash: [6, 4],
                    pointRadius: 0,
                    borderWidth: 2,
                    yAxisID: 'secondary',
                  },
                ]
              : []),
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: { display: true, labels: { color: tickColor, boxWidth: 12 } },
            tooltip: { mode: 'index', intersect: false },
          },
          scales: {
            x: { ticks: { color: tickColor, maxTicksLimit: 8 }, grid: { display: false } },
            y: {
              stacked: true,
              beginAtZero: true,
              title: { display: true, text: yTitle, color: tickColor },
              ticks: { color: tickColor },
              grid: { color: gridColor },
            },
            ...(secondary
              ? {
                  secondary: {
                    position: 'right' as const,
                    beginAtZero: true,
                    title: { display: true, text: secondary.axisTitle, color: tickColor },
                    ticks: { color: tickColor },
                    grid: { display: false },
                  },
                }
              : {}),
          },
        }}
      />
    </div>
  );
}
